import React, { useState } from 'react';
import {
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Box,
  Alert,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Person as PersonIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import ReCAPTCHA from 'react-google-recaptcha';
import { useDispatch } from 'react-redux';
import { authSignUp } from '../../../store/authSlice';
import type { AppDispatch } from 'store/store';

interface FormValues {
  email: string;
  password: string;
  repeatPassword: string;
}

interface SignUpModel {
  Email: string;
  Password: string;
  ConfirmPassword: string;
  ReturnUrl: string;
  GRecaptchaResponse: string;
}

interface SignUpProps {
  onSuccess: () => void;
}

export const SignUp: React.FC<SignUpProps> = ({
  onSuccess,
}): React.ReactElement => {
  const dispatch = useDispatch<AppDispatch>();

  const [error, setError] = useState<string | null>(null);
  const [gRecaptchaResponse, setGRecaptchaResponse] = useState<string | null>(
    null,
  );
  const [formValues, setFormValues] = useState<FormValues>({
    email: '',
    password: '',
    repeatPassword: '',
  });
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState<boolean>(false);

  const handleChange =
    <K extends keyof FormValues>(field: K) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setFormValues({ ...formValues, [field]: event.target.value });
    };

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();

    if (!gRecaptchaResponse) {
      // eslint-disable-next-line no-console
      console.warn('Recaptcha must be checked');
      return;
    }

    if (formValues.password !== formValues.repeatPassword) {
      // You can replace with a Snackbar/Alert UI later
      // eslint-disable-next-line no-alert
      alert('Passwords do not match');
      return;
    }

    const model: SignUpModel = {
      Email: formValues.email,
      Password: formValues.password,
      ConfirmPassword: formValues.repeatPassword,
      ReturnUrl: '/confirm-mail',
      GRecaptchaResponse: gRecaptchaResponse,
    };

    setError(null);
    dispatch(authSignUp(model))
      .unwrap()
      .then(() => {
        onSuccess();
      })
      .catch((err: { message?: string }) => {
        setError(err?.message ?? 'Sign up failed');
        // eslint-disable-next-line no-console
        console.error(err?.message ?? 'Sign up failed');
      });
  };

  const RECAPTCHA_SITE_KEY: string | undefined = (window as any)?.runtimeConfig
    ?.RECAPTCHA_SITE_KEY;

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
      <Typography variant="h5" gutterBottom>
        Sign Up to Planoo
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        variant="outlined"
        label="Email"
        type="email"
        value={formValues.email}
        onChange={handleChange('email')}
        required
        margin="normal"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PersonIcon />
            </InputAdornment>
          ),
        }}
      />

      <TextField
        fullWidth
        variant="outlined"
        label="Password"
        type={showPassword ? 'text' : 'password'}
        value={formValues.password}
        onChange={handleChange('password')}
        required
        margin="normal"
        inputProps={{ minLength: 6 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockIcon />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword((show) => !show)}
                edge="end"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <TextField
        fullWidth
        variant="outlined"
        label="Repeat Password"
        type={showRepeatPassword ? 'text' : 'password'}
        value={formValues.repeatPassword}
        onChange={handleChange('repeatPassword')}
        required
        margin="normal"
        error={
          formValues.repeatPassword.length > 0 &&
          formValues.repeatPassword !== formValues.password
        }
        helperText={
          formValues.repeatPassword.length > 0 &&
          formValues.repeatPassword !== formValues.password
            ? 'Passwords do not match'
            : ''
        }
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockIcon />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowRepeatPassword((show) => !show)}
                edge="end"
                aria-label={
                  showRepeatPassword
                    ? 'Hide repeat password'
                    : 'Show repeat password'
                }
              >
                {showRepeatPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Box sx={{ mt: 2 }}>
        {RECAPTCHA_SITE_KEY ? (
          <ReCAPTCHA
            sitekey={RECAPTCHA_SITE_KEY}
            onChange={(token: string | null) => setGRecaptchaResponse(token)}
          />
        ) : null}
      </Box>

      <Button
        fullWidth
        size="large"
        variant="contained"
        type="submit"
        sx={{ mt: 3, py: 1.5 }}
      >
        Sign Up
      </Button>
    </Box>
  );
};
